/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { CustomDialog, renderCounter, ToolContainer } from '@opengeoweb/shared';
import { ViewPresetMenuListConnect } from './ViewPresetMenuListConnect';
import { useWorkspaceTranslation } from '../../utils/i18n';

export const DIALOG_TITLE = 'View presets menu';

export interface ViewPresetMenuProps {
  panelId: string;
  closeMenu?: () => void;
  isOpen?: boolean;
  initialIsLabelFilterOpen?: boolean;
}

export const ViewPresetMenu: React.FC<ViewPresetMenuProps> = ({
  closeMenu,
  panelId,
  isOpen = false,
  initialIsLabelFilterOpen = true,
}: ViewPresetMenuProps) => {
  const { t } = useWorkspaceTranslation();

  renderCounter.count(ViewPresetMenu.name);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isLabelFilterOpen, setIsLabelFilterOpen] = React.useState(
    initialIsLabelFilterOpen,
  );
  const [hasSpaceForLabelFilter, setHasSpaceForLabelFilter] =
    React.useState(true);

  React.useEffect(() => {
    const parentView =
      containerRef.current?.parentElement ?? containerRef.current ?? undefined;

    if (!parentView || typeof ResizeObserver === 'undefined') {
      return;
    }

    const updateSpace = (width: number): void => {
      if (!Number.isFinite(width) || width <= 0) {
        return;
      }
      setHasSpaceForLabelFilter(width >= 700);
    };

    const widthObserver = new ResizeObserver(([entry]) => {
      updateSpace(entry.contentRect.width);
    });

    updateSpace(parentView.getBoundingClientRect().width);
    widthObserver.observe(parentView);

    // eslint-disable-next-line consistent-return
    return (): void => {
      widthObserver.disconnect();
    };
  }, []);

  React.useEffect(() => {
    if (!hasSpaceForLabelFilter && isLabelFilterOpen) {
      setIsLabelFilterOpen(false);
    }
  }, [hasSpaceForLabelFilter, isLabelFilterOpen]);

  const handleToggleLabelFilter = React.useCallback(() => {
    setIsLabelFilterOpen((prev) => !prev);
  }, []);

  const isLabelFilterVisible = isLabelFilterOpen && hasSpaceForLabelFilter;
  const dialogWidth = isLabelFilterVisible ? 700 : 380;

  return (
    <div ref={containerRef} style={{ height: '100%' }}>
      <CustomDialog
        data-testid="workspace-viewpreset-menu"
        disableEnforceFocus // Fixes not being able to select text in the preset info popper in chrome (they fight over focus)
        hasLayout={false}
        isResizable
        key={isLabelFilterVisible ? 'wide' : 'narrow'}
        minWidth={isLabelFilterVisible ? 700 : undefined}
        onClose={(): void => {
          if (closeMenu) {
            closeMenu();
          }
        }}
        open={isOpen}
        resizableDefaultSize={{ width: dialogWidth, height: '90%' }}
        shouldRenderInline
      >
        <ToolContainer
          onClick={(event): void => {
            event.stopPropagation();
          }}
          onClose={closeMenu}
          onKeyDownCapture={(event): void => {
            if (
              event.key === 'Escape' &&
              event.target instanceof HTMLInputElement
            ) {
              closeMenu?.();
            }
          }}
          style={{ width: '100%', height: '100%' }}
          title={t('workspace-viewpreset-menu')}
        >
          <ViewPresetMenuListConnect
            hasSpaceForLabelFilter={hasSpaceForLabelFilter}
            isLabelFilterOpen={isLabelFilterVisible}
            onToggleLabelFilter={
              hasSpaceForLabelFilter ? handleToggleLabelFilter : undefined
            }
            panelId={panelId}
          />
        </ToolContainer>
      </CustomDialog>
    </div>
  );
};
